import { useEffect, useState } from "react";
import api, { formatDate, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const STATUSES = [
  { key: "nouvelle", label: "Nouvelle" },
  { key: "traitee", label: "Traitée" },
  { key: "archivee", label: "Archivée" },
];

export default function AdminDemandes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/demandes");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = filter === "all" ? items : items.filter((d) => d.status === filter);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/demandes/${id}`, { status });
      setItems((s) => s.map((d) => (d.id === id ? data : d)));
      setSelected(data);
      toast.success("Statut mis à jour");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updateNote = async (id, note) => {
    try {
      const { data } = await api.patch(`/admin/demandes/${id}`, { note_interne: note });
      setItems((s) => s.map((d) => (d.id === id ? data : d)));
      setSelected(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const convert = async (id) => {
    try {
      const client = await api.post(`/admin/demandes/${id}/convert`);
      toast.success("Client créé");
      await load();
      navigate("/admin/clients");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cette demande ?")) return;
    try {
      await api.delete(`/admin/demandes/${id}`);
      setItems((s) => s.filter((d) => d.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Demande supprimée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-demandes-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-eyebrow mb-3">Demandes</p>
          <h1 className="h-display text-4xl md:text-5xl">Boîte de réception.</h1>
        </div>
        <p className="text-serial">{items.length} demande{items.length > 1 ? "s" : ""}</p>
      </div>

      <div className="border-y border-border py-3 mb-8 flex flex-wrap gap-x-6 gap-y-2" data-testid="demandes-filters">
        <button
          className={`text-eyebrow transition-colors ${filter === "all" ? "text-primary" : "text-foreground/60 hover:text-foreground"}`}
          onClick={() => setFilter("all")}
        >
          Toutes
        </button>
        {STATUSES.map((s) => (
          <button
            key={s.key}
            className={`text-eyebrow transition-colors ${filter === s.key ? "text-primary" : "text-foreground/60 hover:text-foreground"}`}
            onClick={() => setFilter(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-7">
          {loading ? (
            <p className="text-serial">Chargement</p>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground text-[14px]">Aucune demande.</p>
          ) : (
            <ul className="border-t border-border" data-testid="demandes-list">
              {filtered.map((d) => (
                <li key={d.id}>
                  <button
                    className={`w-full text-left py-5 px-3 border-b border-border transition-colors ${
                      selected?.id === d.id ? "bg-muted/60" : "hover:bg-muted/30"
                    }`}
                    onClick={() => setSelected(d)}
                    data-testid={`demande-row-${d.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-serif text-xl leading-tight">{d.nom}</p>
                        <p className="text-[12.5px] text-muted-foreground mt-1">
                          {d.email}
                          {d.telephone ? ` · ${d.telephone}` : ""}
                        </p>
                        <p className="text-serial mt-2">
                          {d.cible} · {formatDate(d.created_at)}
                        </p>
                      </div>
                      <span className={`text-eyebrow shrink-0 ${d.status === "nouvelle" ? "text-primary" : "text-muted-foreground"}`}>
                        {d.status}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <aside className="md:col-span-5 md:sticky md:top-8 self-start">
          {selected ? (
            <div className="border border-border p-6" data-testid="demande-detail">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-serial">{formatDate(selected.created_at)}</p>
                  <p className="font-serif text-2xl mt-2 leading-tight">{selected.nom}</p>
                  <p className="text-[13px] text-muted-foreground mt-1">{selected.email}</p>
                  {selected.telephone && <p className="text-[13px] text-muted-foreground">{selected.telephone}</p>}
                </div>
                <button onClick={() => remove(selected.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid="demande-delete">
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="space-y-2 my-5 text-[13.5px]">
                {selected.cible === "expertise" && (
                  <>
                    {selected.compagnie && <Row label="Compagnie">{selected.compagnie}</Row>}
                    {selected.numero_sinistre && <Row label="N° sinistre">{selected.numero_sinistre}</Row>}
                    {selected.nature_sinistre && <Row label="Nature">{selected.nature_sinistre}</Row>}
                  </>
                )}
                {selected.cible === "particulier" && selected.type_projet && (
                  <Row label="Type de projet">{selected.type_projet}</Row>
                )}
                {selected.cible === "professionnel" && selected.type_etablissement && (
                  <Row label="Établissement">{selected.type_etablissement}</Row>
                )}
              </div>

              <div className="border-t border-border pt-4">
                <p className="text-eyebrow mb-2">Message</p>
                <p className="text-[14px] leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>

              <div className="border-t border-border mt-5 pt-4">
                <p className="text-eyebrow mb-2">Note interne</p>
                <textarea
                  className="w-full bg-transparent border border-border p-3 text-[13px] min-h-[80px]"
                  defaultValue={selected.note_interne || ""}
                  onBlur={(e) => updateNote(selected.id, e.target.value)}
                  placeholder="Ajouter une note (sauvegarde automatique au clic ailleurs)…"
                  data-testid="demande-note"
                />
              </div>

              <div className="border-t border-border mt-5 pt-4">
                <p className="text-eyebrow mb-3">Statut</p>
                <div className="grid grid-cols-2 gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s.key}
                      onClick={() => updateStatus(selected.id, s.key)}
                      className={`text-[12px] py-2 transition-colors ${
                        selected.status === s.key ? "bg-foreground text-background" : "border border-border hover:bg-muted/60"
                      }`}
                      data-testid={`status-${s.key}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {!selected.client_id && (
                <button onClick={() => convert(selected.id)} className="btn-terra w-full justify-center mt-6" data-testid="demande-convert">
                  Convertir en client
                </button>
              )}
              {selected.client_id && (
                <p className="text-eyebrow text-primary mt-6">Convertie en client</p>
              )}
            </div>
          ) : (
            <div className="border border-dashed border-border p-8 text-center text-muted-foreground text-[14px]">
              Sélectionnez une demande pour voir le détail.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-serial shrink-0">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}
